/**
 * Widget Adapter Interface
 * Defines the contract that all widget adapters must implement
 * This allows FilterComponents to work with any widget type through a unified interface
 */

/**
 * Abstract base class for all widget adapters
 * Defines the interface that all adapters must implement
 */
export class WidgetAdapter {
    constructor(widget) {
        if (this.constructor === WidgetAdapter) {
            throw new Error(
                'WidgetAdapter is an abstract class and cannot be instantiated directly'
            );
        }
        this.widget = widget;
    }

    /**
     * Check if the widget has an active value (for filtering)
     * @returns {boolean} True if widget has a non-empty value
     */
    hasValue() {
        throw new Error('hasValue must be implemented by derived class');
    }

    /**
     * Get the current value of the widget
     * @returns {*} Current widget value
     */
    getValue() {
        throw new Error('getValue must be implemented by derived class');
    }

    /**
     * Set the value of the widget
     * @param {*} value Value to set
     * @returns {boolean} True if value was set successfully
     */
    setValue() {
        throw new Error('setValue must be implemented by derived class');
    }

    /**
     * Clear the widget's value
     * @returns {boolean} True if value was cleared successfully
     */
    clearValue() {
        throw new Error('clearValue must be implemented by derived class');
    }

    /**
     * Bind an event handler to the widget
     * @param {string} eventName Event name (e.g., 'change', 'dataBound')
     * @param {Function} handler Event handler function
     */
    bind() {
        throw new Error('bind must be implemented by derived class');
    }

    /**
     * Check if the widget is available/initialized
     * @returns {boolean} True if widget is available
     */
    isAvailable() {
        return this.widget !== null && this.widget !== undefined;
    }

    /**
     * Get the underlying widget instance
     * @returns {*} The underlying widget
     */
    getWidget() {
        return this.widget;
    }
}

/**
 * Text Input Adapter
 * Adapter for plain HTML text inputs
 */
export class TextInputAdapter extends WidgetAdapter {
    constructor(element) {
        super(element);
        this.element = element;
    }

    hasValue() {
        if (!this.isAvailable()) return false;
        const value = $(this.element).val();
        return value && value.trim() !== '';
    }

    getValue() {
        if (!this.isAvailable()) return '';
        return $(this.element).val();
    }

    setValue(value) {
        if (!this.isAvailable()) return false;
        $(this.element).val(value || '');
        return true;
    }

    clearValue() {
        return this.setValue('');
    }

    bind(eventName, handler) {
        if (!this.isAvailable()) return;
        $(this.element).on(eventName, handler);
    }
}

/**
 * Native Select Adapter
 * Adapter for plain HTML <select> elements (not Kendo widgets)
 */
export class NativeSelectAdapter extends WidgetAdapter {
    constructor(element) {
        super(element);
        this.element = element;
    }

    hasValue() {
        if (!this.isAvailable()) return false;
        const value = $(this.element).val();
        return value !== null && value !== undefined && value !== '';
    }

    getValue() {
        if (!this.isAvailable()) return '';
        return $(this.element).val();
    }

    setValue(value) {
        if (!this.isAvailable()) return false;
        $(this.element).val(value || '');
        return true;
    }

    clearValue() {
        if (!this.isAvailable()) return false;
        $(this.element).val('');
        return true;
    }

    bind(eventName, handler) {
        if (!this.isAvailable()) return;
        $(this.element).on('change', handler);
    }
}

/**
 * Kendo Widget Adapter
 * Base adapter for all Kendo UI widgets
 */
export class KendoWidgetAdapter extends WidgetAdapter {
    constructor(widget) {
        super(widget);
        // Store pending event bindings for when widget becomes available
        this._pendingBindings = [];
    }

    hasValue() {
        if (!this.isAvailable()) return false;
        const value = this.widget.value();
        return this._isValueActive(value);
    }

    getValue() {
        if (!this.isAvailable()) return null;
        return this.widget.value();
    }

    setValue(value) {
        if (!this.isAvailable()) return false;
        this.widget.value(value);
        return true;
    }

    clearValue() {
        if (!this.isAvailable()) return false;
        this.widget.value(this._getDefaultClearValue());
        return true;
    }

    bind(eventName, handler) {
        if (!this.isAvailable()) {
            // Store pending binding for when widget becomes available
            this._pendingBindings = this._pendingBindings || [];
            this._pendingBindings.push({ eventName, handler });
            return;
        }
        if (this.widget.bind) {
            this.widget.bind(eventName, handler);
        }
    }

    /**
     * Apply any pending event bindings after widget becomes available
     * Called by AdapterFactory.updatePendingAdapter
     */
    applyPendingBindings() {
        if (!this.isAvailable() || !this._pendingBindings) return;

        this._pendingBindings.forEach(({ eventName, handler }) => {
            if (this.widget.bind) {
                this.widget.bind(eventName, handler);
            }
        });

        // Clear pending bindings after applying
        this._pendingBindings = [];
    }

    /**
     * Check if a value is considered "active" for filtering
     * Override in derived classes for specific logic
     * @param {*} value Value to check
     * @returns {boolean} True if value is active
     */
    _isValueActive(value) {
        return value !== null && value !== undefined && value !== '';
    }

    /**
     * Get the default value to use when clearing
     * Override in derived classes for specific logic
     * @returns {*} Default clear value
     */
    _getDefaultClearValue() {
        return '';
    }
}

/**
 * Kendo DropDownList Adapter
 */
export class KendoDropDownListAdapter extends KendoWidgetAdapter {
    _isValueActive(value) {
        return value && value !== '';
    }
}

/**
 * Kendo MultiSelect Adapter
 */
export class KendoMultiSelectAdapter extends KendoWidgetAdapter {
    _isValueActive(value) {
        return Array.isArray(value) && value.length > 0;
    }

    _getDefaultClearValue() {
        return [];
    }
}

/**
 * Kendo ComboBox Adapter
 */
export class KendoComboBoxAdapter extends KendoWidgetAdapter {
    _isValueActive(value) {
        return value && value !== '';
    }
}

/**
 * Kendo Switch Adapter
 */
export class KendoSwitchAdapter extends KendoWidgetAdapter {
    _isValueActive(value) {
        return value !== null && value !== undefined;
    }

    _getDefaultClearValue() {
        return false;
    }
}

/**
 * Kendo DatePicker Adapter
 */
export class KendoDatePickerAdapter extends KendoWidgetAdapter {
    _isValueActive(value) {
        return value !== null;
    }

    _getDefaultClearValue() {
        return null;
    }
}

/**
 * Kendo DateRangePicker Adapter
 */
export class KendoDateRangePickerAdapter extends KendoWidgetAdapter {
    /**
     * Check if the date range has any value set
     */
    hasValue() {
        if (!this.isAvailable()) return false;
        const range = this.widget.range();
        if (!range) return false;
        return range.start !== null || range.end !== null;
    }

    /**
     * Get the current range value as {start, end} with ISO date strings
     * @returns {{ start: string|null, end: string|null }|null}
     */
    getValue() {
        if (!this.isAvailable()) return null;
        const range = this.widget.range();
        if (!range) return null;
        return {
            start: this._toISODate(range.start),
            end: this._toISODate(range.end)
        };
    }

    /**
     * Set the range value from a {start, end} object (ISO strings or Date objects)
     * @param {{ start: string|Date|null, end: string|Date|null }} value
     */
    setValue(value) {
        if (!this.isAvailable()) return false;
        if (value && typeof value === 'object') {
            const range = {
                start: value.start ? new Date(value.start) : null,
                end: value.end ? new Date(value.end) : null
            };
            this.widget.range(range);
            this._syncClearButtons(range);
            this._ensureCalendarNavigation();
        }
        return true;
    }

    /**
     * Synchronize clear button visibility after programmatic range() call.
     * The widget's range() method does not toggle clear buttons internally
     * (only user-initiated _startChange/_endChange handlers do),
     * so we call _toggleClearButton manually.
     */
    _syncClearButtons(range) {
        if (typeof this.widget._toggleClearButton === 'function') {
            this.widget._toggleClearButton('start', range.start !== null);
            this.widget._toggleClearButton('end', range.end !== null);
        }
    }

    /**
     * Ensure the calendar popup navigates to the selected range when opened.
     * When range is set programmatically (e.g., restored from session),
     * the calendar may still open at the current month. Re-applying the range
     * in the 'open' event forces correct calendar navigation.
     */
    _ensureCalendarNavigation() {
        if (this._openNavigationBound) return;

        this.widget.bind('open', () => {
            const currentRange = this.widget.range();
            if (currentRange && (currentRange.start || currentRange.end)) {
                this.widget.range(currentRange);
            }
        });

        this._openNavigationBound = true;
    }

    clearValue() {
        if (!this.isAvailable()) return false;
        this.widget.range({ start: null, end: null });
        this._syncClearButtons({ start: null, end: null });
        return true;
    }

    /**
     * Returns individual filter entries for server-side model binding.
     * Uses the widget's startField/endField options to determine property names.
     * @returns {Array<[string, string]>} Array of [fieldName, isoDateString] pairs
     */
    getFilterEntries() {
        if (!this.isAvailable()) return [];

        const range = this.widget.range();
        if (!range) return [];

        const startField = this.widget.options.startField;
        const endField = this.widget.options.endField;
        const entries = [];

        if (range.start) {
            entries.push([startField, this._toISODate(range.start)]);
        }
        if (range.end) {
            entries.push([endField, this._toISODate(range.end)]);
        }

        return entries;
    }

    /**
     * Indicates this is a compound adapter that produces multiple filter entries
     */
    get isCompound() {
        return true;
    }

    _isValueActive(value) {
        if (value === null || value === undefined) return false;
        if (typeof value === 'object') {
            return value.start !== null || value.end !== null;
        }
        return true;
    }

    _getDefaultClearValue() {
        return null;
    }

    /**
     * Convert a Date object to ISO date string (yyyy-MM-dd)
     * @param {Date|null} date
     * @returns {string|null}
     */
    _toISODate(date) {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
